export const dispose = (disposable: Disposable) => {
  disposable[Symbol.dispose]()
}

export const disposeAsync = async (disposable: AsyncDisposable) => {
  await disposable[Symbol.asyncDispose]()
}

export const createDisposable = (dispose: () => void): Disposable => {
  return {
    [Symbol.dispose]: dispose,
  }
}

export const createAsyncDisposable = (dispose: () => Promise<void>): AsyncDisposable => {
  return {
    [Symbol.asyncDispose]: dispose,
  }
}

/**
 * Build a stack and hand ownership out. `using` + `move()` in one place, so the `move()` cannot
 * be forgotten: a throw half way disposes what was registered and reaches the caller.
 */
export const withDisposableStack = (cb: (stack: DisposableStack) => void) => {
  using stack = new DisposableStack()
  cb(stack)
  return stack.move()
}

/** `withDisposableStack` for setup that has to await, and whose caller awaits the result. */
export const withDisposableStackAsync = async (cb: (stack: DisposableStack) => Promise<void>) => {
  using stack = new DisposableStack()
  await cb(stack)
  return stack.move()
}

export function withDispose<T extends {}>(value: T, dispose: () => void): T & Disposable {
  let disposed = false
  const d1 = dispose
  const d2 = value[Symbol.dispose]
  dispose = () => {
    if (disposed) return
    disposed = true
    d1()
    d2?.()
  }
  value[Symbol.dispose] = dispose
  return value as T & Disposable
}

export function withAsyncDispose<T extends {}>(value: T, dispose: () => Promise<void>): T & AsyncDisposable {
  let disposed = false
  const d1 = dispose
  const d2 = value[Symbol.dispose]
  dispose = async () => {
    if (disposed) return
    disposed = true
    await d1()
    await d2?.()
  }
  value[Symbol.asyncDispose] = dispose
  return value as T & AsyncDisposable
}
